import React, { useState } from "react";
import { Container, Card, CardHeader, CardBody, Collapse } from "reactstrap";
import { ChevronDown, ChevronUp } from "lucide-react";

const educationData = [
  {
    title: "1. What is melanoma?",
    content: (
      <>
        <p><strong>Melanoma</strong> is a serious type of skin cancer that begins in melanocytes—the specialised cells responsible for producing melanin, the pigment that gives skin its colour and helps protect it from ultraviolet (UV) radiation.</p>
        <p>It often starts on the skin’s surface, either within a pre-existing mole or in otherwise normal-looking skin, and can usually be treated effectively if detected early. However, if left untreated, melanoma can spread to other parts of the body.</p>

        <h5 className="mt-4 font-weight-bold">Causes</h5>
        <p>Around 95% of melanomas are caused by overexposure to UV radiation, whether from natural sunlight or artificial sources such as sunbeds. UV radiation damages the DNA in melanocytes, and over time this damage can trigger the uncontrolled cell growth that leads to melanoma.</p>
        <p>A number of factors are known to increase the risk of developing melanoma:</p>
        <ul>
          <li><strong>Sun exposure and sunburn</strong> — unprotected exposure to UV radiation is the leading risk factor for melanoma. It is intermittent, intense exposure rather than regular, moderate exposure that carries the greatest melanoma risk. A history of severe sunburn is particularly significant: five or more blistering sunburns before the age of 20 substantially increases risk, and severe sunburns in childhood and adolescence may double the risk of melanoma in adulthood. People who work or spend significant leisure time outdoors also face an increased risk through higher overall UV exposure.</li>
          <li><strong>Moles</strong> — having a large number of moles or moles that appear unusual in shape, size or colour is associated with a higher risk of melanoma.</li>
          <li><strong>Personal and medical history</strong> — a previous diagnosis of skin cancer, or a condition or treatment that affects the immune system (immunosuppression), increases the risk of melanoma developing.</li>
          <li><strong>Skin type and colouring</strong> — people with fair skin, red or fair hair, blue or green eyes or skin that burns easily in the sun are at greatest risk.</li>
          <li><strong>Family history and genetics</strong> — having a close relative who has had melanoma — particularly if they were diagnosed under the age of 40 — raises individual risk. Certain inherited genetic variations are also associated with susceptibility to melanoma.</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">How melanoma spreads</h5>
        <p>If left undetected and untreated, melanoma can grow deeper into the skin and into the lymphatic system or bloodstream. Once it does, cancer cells can travel to distant parts of the body, most commonly the lungs, liver, brain, or bones. This process is known as <strong>metastasis</strong>, and melanoma that has spread in this way is referred to as <strong>metastatic</strong> or <strong>stage 4 melanoma</strong>.</p>
        <p>This is why early detection matters so much. Melanoma caught at an early stage, before it has grown beyond the skin’s surface, is highly treatable. The deeper it grows, the greater the risk of spread.</p>
      </>
    )
  },
  {
    title: "2. Understanding the skin",
    content: (
      <>
        <p>Your skin is your body’s largest organ, acting as a protective barrier against injury, infection, and UV damage.</p>
        <p>It has two main layers:</p>
        <ul>
          <li><strong>Epidermis</strong> — the outer layer</li>
          <li><strong>Dermis</strong> — the deeper inner layer</li>
        </ul>

        <p>In the epidermis, three main types of cells exist:</p>
        <ul>
          <li><strong>Squamous cells</strong> — flat cells at the surface</li>
          <li><strong>Basal cells</strong> — rounder cells beneath squamous cells</li>
          <li><strong>Melanocytes</strong> — the pigment-producing cells found between basal cells from which melanoma develops.</li>
        </ul>

        <p>Non-melanoma skin cancers such as basal cell carcinoma and squamous cell carcinoma originate in the other cell types.</p>
        <p>The dermis contains blood vessels, lymphatic vessels, sweat glands, and hair follicles. These are significant in the context of melanoma: once it grows deep enough to reach the dermis, it can access the bloodstream and lymphatic system, which is how it spreads.</p>
      </>
    )
  },
  {
    title: "3. Staging - Clinical vs Histological",
    content: (
      <>
        <p>Staging is one of the most important steps after a melanoma diagnosis. It gives your medical team a clear picture of the tumour — how deep it is, how it’s behaving at a cellular level, and whether it has spread anywhere beyond the original site.</p>
        <p className="font-weight-bold">This information directly shapes the treatment plan recommended for you.</p>
        <p>There are two types of staging used in melanoma: <strong>histological staging</strong> and <strong>clinical staging</strong>. Both are reviewed and discussed by your multidisciplinary team (MDT) before an overall stage is agreed.</p>

        <div className="p-4 my-4" style={{ backgroundColor: "#FDFEFE", borderLeft: "4px solid #0284c7", borderRadius: "8px" }}>
          <h6 className="font-weight-bold mb-3">Stages of Melanoma</h6>
          <ul className="mb-0">
            <li><strong>Stage 0:</strong> Melanoma confined to epidermal region of skin</li>
            <li><strong>Stage I:</strong> Localised disease, only in skin and very thin</li>
            <li><strong>Stage II:</strong> Localised disease, thicker than Stage I</li>
            <li><strong>Stage III:</strong> Spread to lymph nodes</li>
            <li><strong>Stage IV:</strong> Spread to other organs</li>
          </ul>
        </div>

        <h5 className="mt-4 font-weight-bold">Histological staging</h5>
        <p>Histological staging is usually the first step. It involves examining a sample of the melanoma under a microscope, a process carried out by a <strong>histopathologist</strong> — a doctor who specialises in analysing tissue at the cellular level.</p>
        <p>Two key features are assessed at this stage:</p>
        <ul>
          <li><strong>Breslow depth</strong> — how deeply the melanoma has grown into the layers of the skin</li>
          <li><strong>Ulceration</strong> — whether the surface of the skin over the tumour has broken down</li>
        </ul>
        <p>Both of these factors play a significant role in determining your treatment plan. You can read more about how to interpret your histology results in our guide to reading your pathology report.</p>
        <p>Histological examination can also identify the <strong>subtype</strong> of melanoma. There are four main subtypes:</p>
        <ul>
          <li>Superficial spreading melanoma</li>
          <li>Nodular melanoma</li>
          <li>Lentigo maligna melanoma</li>
          <li>Acral lentiginous melanoma</li>
        </ul>
        <p>In addition to subtype, the pathologist can assess how the cells are likely to behave, giving your team some insight into how aggressive the melanoma may be.</p>

        <h5 className="mt-4 font-weight-bold">Clinical staging</h5>
        <p>Clinical staging looks at a broader picture: how thick the tumour is, and whether the melanoma has spread beyond its original site. This guides decisions about further treatment, which may include additional surgery, systemic therapy, or close monitoring.</p>
        <p>Clinical staging may involve:</p>
        <ul>
          <li>A <strong>sentinel lymph node biopsy</strong> — to check whether melanoma cells have reached the nearby lymph nodes</li>
          <li>A <strong>PET-CT scan</strong> — to check for spread to other parts of the body</li>
        </ul>
        <p>This information is used to determine your clinical stage using the internationally recognised <strong>TNM staging system</strong> — based on the Tumour, Nodes, and Metastases.</p>

        <h5 className="mt-4 font-weight-bold">The TNM staging system explained</h5>

        <div className="p-4 my-4" style={{ backgroundColor: "#EBF5FB", borderLeft: "4px solid #0284c7", borderRadius: "8px" }}>
          <p className="mb-0"><strong>What is the TNM system?</strong> TNM is a standardised way of describing how advanced a cancer is, used by oncology teams worldwide. Each letter captures a different aspect of how the melanoma has behaved: the size and depth of the original tumour (T), whether it has reached the lymph nodes (N), and whether it has spread further in the body (M).</p>
        </div>

        <h6 className="mt-4 font-weight-bold">T</h6>
        <p>The <strong>T stage</strong> describes the thickness of the melanoma (Breslow depth). It is further subdivided depending on whether <strong>ulceration</strong> (a breakdown of the overlying skin) is present. Ulceration is an important prognostic factor, and its presence moves the T category up by one sub-stage.</p>

        <h6 className="mt-4 font-weight-bold">N</h6>
        <p>The <strong>N stage</strong> describes whether melanoma cells have been found in the nearby <strong>lymph nodes</strong>, and how many nodes are involved. Your team will also look for evidence of spread in the area between the primary tumour and the nearest lymph nodes. There are three specific patterns of this kind of spread:</p>
        <ul>
          <li><strong>Microsatellite metastases</strong> — melanoma cells visible under the microscope adjacent to the primary tumour</li>
          <li><strong>Satellite metastases</strong> — visible spread to an area less than 2 cm from the primary tumour</li>
          <li><strong>In-transit metastases</strong> — spread to an area more than 2 cm from the primary tumour, but before the nearest lymph nodes</li>
        </ul>
        <p>The N stage is determined by the number of lymph nodes affected and/or the presence of any of the patterns above.</p>

        <h6 className="mt-4 font-weight-bold">M</h6>
        <p>The <strong>M stage</strong> describes whether the melanoma has spread to a distant part of the body, known as <strong>distant metastasis</strong>. There are two categories:</p>

        <table className="table table-bordered my-3">
          <thead>
            <tr>
              <th>Stage</th>
              <th>What it means</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>M0</strong></td>
              <td>No spread to distant parts of the body has been detected</td>
            </tr>
            <tr>
              <td><strong>M1</strong></td>
              <td>The melanoma has spread to a distant part of the body</td>
            </tr>
          </tbody>
        </table>

        <p>M1 is further subdivided (M1a–M1d) depending on where the spread has occurred — for example, to the lungs, other organs, or the central nervous system.</p>

        <div className="p-4 mt-4" style={{ backgroundColor: "#FDFEFE", borderLeft: "4px solid #0284c7", borderRadius: "8px" }}>
          <h6 className="font-weight-bold">Talking to your team:</h6>
          <p className="mb-0">Staging can feel like a lot of information to take in at once. Don’t hesitate to ask your MDT team to walk you through your individual staging results. Understanding your stage can help you feel more prepared when discussing treatment options.</p>
        </div>
      </>
    )
  },
  {
    title: "4. What is a Sentinel Lymph Node?",
    content: (
      <>
        <p>Your <strong>lymphatic system</strong> is part of your immune system, which helps your body fight infections. It includes the network of vessels that carry fluid called <strong>lymph</strong>, which contains white blood cells. Along this network are <strong>lymph nodes</strong> — small, bean-shaped glands that act like filters, trapping harmful substances such as bacteria or cancer cells.</p>
        <p>The <strong>sentinel lymph node</strong> is the <strong>first lymph node</strong> that drains lymph from the area of skin where a <strong>melanoma</strong> has developed. It acts like the gatekeeper of your lymphatic system in the area around your melanoma. This node is the most likely place for melanoma cells to travel to first if the cancer begins to spread, because it is the first stop for lymph draining from the tumour.</p>
        <p>Watch the Melanoma Focus video — <a href="https://www.youtube.com/watch?v=1lhTvRYZQX4" target="_blank" rel="noopener noreferrer">Understanding Lymph Node Biopsy</a></p>

        <h5 className="mt-4 font-weight-bold">What is a Sentinel Lymph Node Biopsy?</h5>
        <p>A <strong>sentinel lymph node biopsy</strong> (SNB) is a procedure used to find out whether melanoma has spread to nearby lymph nodes. It’s usually recommended if your melanoma is deeper than Breslow thickness 0.8 mm, or if there are other risk factors present, such as ulceration (the breakdown of skin over the melanoma), and mitotic rate (how quickly cancer cells are dividing). Patient age and overall health can also influence the decision to perform a sentinel node biopsy.</p>

        <h5 className="mt-4 font-weight-bold">What Happens During a Sentinel Lymph Node Biopsy?</h5>
        <ul>
          <li>A small amount of <strong>radioactive dye</strong> is injected into the melanoma site.</li>
          <li>A <strong>scan</strong> is done to locate the lymph node that first absorbs the dye.</li>
          <li>In the operating theatre, a <strong>blue dye</strong> is also injected to help the surgeon visually locate the node.</li>
          <li>This <strong>sentinel node</strong> (sometimes more than one) is removed and examined under a microscope.</li>
        </ul>
        <p>The biopsy is typically performed under <strong>general anaesthetic</strong> at the same time as your <strong>wide local excision</strong>.</p>
        <p>Watch the Melanoma Focus video — <a href="https://www.youtube.com/watch?v=CgQYZzskTAo" target="_blank" rel="noopener noreferrer">What Happens During a SNB</a></p>

        <h5 className="mt-4 font-weight-bold">What Happens After Surgery?</h5>
        <ul>
          <li>Most patients go <strong>home the same day</strong>.</li>
          <li>You may need <strong>up to a week off work</strong>, depending on where the surgery was done, or if your job requires heavy lifting or manual work.</li>
          <li><strong>Pain is usually mild</strong> and can be managed with over-the-counter pain relief.</li>
          <li>You’ll receive <strong>wound care advice</strong> and might need a <strong>follow-up appointment</strong> for dressing changes.</li>
          <li><strong>Scarring</strong> can take a few months to settle.</li>
          <li>Most people return to normal activities within a week, but you should follow your surgeon’s specific advice on this.</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">Possible Side Effects</h5>
        <div className="p-4 my-3" style={{ backgroundColor: "#FDFEFE", borderLeft: "4px solid #0284c7", borderRadius: "8px" }}>
          <p className="mb-2">While most people recover well, possible side effects include:</p>
          <ul className="mb-0">
            <li>Infection</li>
            <li>Bleeding</li>
            <li>Numbness</li>
            <li>Fluid build-up (seroma)</li>
            <li>Scarring</li>
            <li>Lymphoedema (persistent swelling of a limb) — rare</li>
            <li>Allergic reaction to the dye — rare (1 in 1,000 cases)</li>
          </ul>
        </div>

        <h5 className="mt-4 font-weight-bold">What If My Sentinel Node Is Positive?</h5>
        <p>If <strong>melanoma cells are found</strong>, it means the cancer <strong>has spread</strong>, and the stage of your melanoma will change. You’ll likely be referred to a Consultant Oncologist to discuss further treatment options, including immunotherapy and targeted therapies, and you may be offered clinical trials.</p>

        <h5 className="mt-4 font-weight-bold">Why not biopsy all lymph nodes?</h5>
        <p>Previously, patients with a positive sentinel node would have been offered further surgery to remove all lymph nodes in the area. Evidence has shown that the immediate removal of all lymph nodes doesn’t necessarily improve survival and can increase complications like swelling (lymphoedema) and nerve damage. Instead close monitoring with focused ultrasound surveillance will be offered to monitor the area where your sentinel lymph node was taken (e.g., underarm or groin). You will also be offered some staging scans such as a PET CT and an MRI on the brain.</p>

        <h5 className="mt-4 font-weight-bold">Important to Remember</h5>
        <p>A sentinel node biopsy is a <strong>diagnostic test</strong>.</p>
        <p>Like all tests, there is a small error rate. A negative result does not guarantee the melanoma will never come back or spread, but it significantly lowers that risk.</p>
      </>
    )
  },
  {
    title: "5. Managing your Mental Health",
    content: (
      <>
        <p>Hearing the words <strong>“you have melanoma”</strong> can feel overwhelming. Many people describe it as one of the most frightening moments of their lives. Whether your melanoma has been found early or is more advanced, it is completely normal to experience a wide range of emotions — including fear, sadness, anger, confusion, or even numbness.</p>
        <p>Strong emotional reactions do not mean you are weak. They mean you are <strong>human</strong>.</p>
        <p>Looking after your emotional wellbeing is just as important as attending appointments and undergoing treatment. How you feel mentally can affect your sleep, your energy, your relationships, and your ability to cope day to day.</p>
        <p className="font-weight-bold">You do not have to manage this alone.</p>

        <h5 className="mt-4 font-weight-bold">Common Emotional Reactions</h5>
        <p>Everyone reacts differently, but many people diagnosed with melanoma experience:</p>
        <ul>
          <li><strong>Shock or disbelief</strong> — especially if you felt completely well before diagnosis.</li>
          <li><strong>Anxiety and fear</strong> — about treatment, results, follow-up appointments, or what the future may hold.</li>
          <li><strong>Fear of recurrence</strong> — it is very common to feel particularly anxious before scans or follow-up visits (sometimes called “scanxiety”).</li>
          <li><strong>Sadness or low mood</strong> — you may feel you are grieving the loss of your previous sense of health or certainty.</li>
          <li><strong>Anger or frustration</strong> — you may wonder, “Why me?”</li>
          <li><strong>Guilt or self-blame</strong> — some people worry about past sun exposure. It is important to remember that melanoma is complex and self-blame is neither helpful nor accurate.</li>
          <li><strong>Increased vigilance</strong> — you may find yourself frequently checking your skin or worrying about normal changes.</li>
        </ul>
        <p>These feelings often come and go. They may change over time, especially at key points such as diagnosis, surgery, treatment decisions, or follow-up appointments.</p>
        <p className="font-weight-bold">All of these reactions are normal.</p>

        <h5 className="mt-4 font-weight-bold">Ways to Support Your Emotional Health</h5>
        <ul>
          <li><strong>Give Yourself Permission to Feel</strong> — there is no ‘right’ way to cope. It’s okay to cry, to feel angry, or to have days when you just want to rest.</li>
          <li><strong>Gather the Facts</strong> — understanding your diagnosis and treatment plan can reduce uncertainty. Bring a friend or family member to appointments and write down your questions. If searching online, use trusted Irish or international cancer resources. Limiting unstructured internet searching can reduce unnecessary anxiety.</li>
          <li><strong>Talk About It</strong> — sharing your feelings with people you trust — family, friends, other melanoma patients — can lift a weight off your shoulders.</li>
          <li><strong>Consider Professional Support</strong>
            <ul className="mt-2">
              <li><strong>Counselling or psychotherapy</strong> can help you process difficult emotions.</li>
              <li>The <strong>Irish Cancer Society</strong> offers free counselling sessions.</li>
              <li><strong>Psycho-oncology services</strong> are available in some cancer centres — ask your team for a referral.</li>
            </ul>
          </li>
          <li><strong>Look After Your Body</strong> — exercise, healthy eating, and good sleep are proven to boost mood and energy levels. Even gentle daily walks can help reduce anxiety.</li>
          <li><strong>Practice Mindfulness or Relaxation</strong> — meditation, deep breathing, and yoga can help manage stress and improve focus.</li>
        </ul>

        <p className="mt-4 font-weight-bold text-danger">When to Seek Extra Help:</p>
        <p>If you experience low mood most days for over two weeks, trouble sleeping, hopelessness, or thoughts of self-harm, please speak to your GP or oncology team early.</p>

        <p className="mt-4 font-italic text-muted">This information is intended as general guidance and does not replace advice from your medical team.</p>
      </>
    )
  },
  {
    title: "6. Discussing your diagnosis with friends/family",
    content: (
      <>
        <p>Finding out you have melanoma can feel overwhelming, and telling your family can be one of the hardest steps. There’s no “perfect” way to have this conversation — what matters most is being honest, gentle, and giving everyone time to take in the news.</p>

        <h5 className="mt-4 font-weight-bold">1. Give Yourself a Moment First</h5>
        <p>You don’t need to tell everyone immediately after you receive your diagnosis. Take some time to process the news yourself, gather the facts from your doctor, and think about what you want to say.</p>

        <h5 className="mt-4 font-weight-bold">2. Choose the Right Time and Place</h5>
        <ul>
          <li>Find a quiet, private space where you won’t be interrupted.</li>
          <li>If possible, speak in person rather than over text or email.</li>
          <li>Choose a time when you and your family are not rushed or distracted.</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">3. Be Honest but Gentle</h5>
        <ul>
          <li>Use clear, simple language:
            <div className="ml-3 mt-2 mb-2 pl-3" style={{ borderLeft: "3px solid #0284c7", fontStyle: "italic" }}>
              “I’ve been diagnosed with melanoma, which is a type of skin cancer. My doctors have explained my treatment plan, and I want to share that with you.”
            </div>
          </li>
          <li>Avoid overwhelming them with too much medical detail at once — you can give more information later.</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">4. Prepare for Emotional Reactions</h5>
        <p>People may react with shock, sadness, anger, or silence. These emotions are normal.</p>
        <ul>
          <li>Allow space for them to express how they feel.</li>
          <li>If you don’t know the answer to a question, it’s okay to say, “I’m not sure yet, but I can find out.”</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">5. Share the Plan</h5>
        <p>Let your family know what will happen next — for example:</p>
        <ul>
          <li>Upcoming surgery or treatment dates</li>
          <li>What support you might need (e.g. lifts to appointments, help at home)</li>
          <li>Reassurance that melanoma treatment can be very effective, especially when caught early</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">6. Ask for the Support You Need</h5>
        <p>Sometimes family members want to help but don’t know how. You can suggest:</p>
        <ul>
          <li>Coming with you to appointments</li>
          <li>Helping with meals, childcare, or household tasks</li>
          <li>Simply being there to listen</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">7. Talking to Children</h5>
        <ul>
          <li>Use age-appropriate, honest language.</li>
          <li>Reassure them they are not to blame and that cancer is not contagious.</li>
          <li>Be prepared for repeated questions as they process the news.</li>
          <li>The <strong>Irish Cancer Society</strong> offers guidance and booklets for talking to children about cancer (<a href="https://www.cancer.ie/" target="_blank" rel="noopener noreferrer">www.cancer.ie</a>).</li>
        </ul>

        <h5 className="mt-4 font-weight-bold">8. Look After Yourself Too</h5>
        <p>These conversations can be emotionally draining. After telling loved ones:</p>
        <ul>
          <li>Take some quiet time to rest.</li>
          <li>Speak with a counsellor or support group if you need help coping with reactions.</li>
          <li>Remember: you are not alone — support services are here for both you and your family.</li>
        </ul>

        <div className="p-4 mt-4" style={{ backgroundColor: "#FDFEFE", borderLeft: "4px solid #0284c7", borderRadius: "8px" }}>
          <h6 className="font-weight-bold">Further Support:</h6>
          <p className="mb-1">
            <a href="https://www.cancer.ie/support-after-diagnosis" target="_blank" rel="noopener noreferrer"><strong>Irish Cancer Society</strong></a> <strong>Freephone Support Line:</strong> 1800 200 700
          </p>
          <p className="mb-1"><strong>Daffodil Centres</strong> — drop-in information and emotional support in many Irish hospitals</p>
          <p className="mb-0">
            <a href="https://www.facebook.com/MelanomaSupportIreland/" target="_blank" rel="noopener noreferrer"><strong>Melanoma Support Ireland</strong></a> — private online peer group for patients and families
          </p>
        </div>
      </>
    )
  }
];

const MelanomaEducation = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  return (
    <div className="education-section py-5" style={{ backgroundColor: "#FFFFFF" }} id="education">
      <Container>
        <div className="text-center mb-5 animate-on-scroll">
          <span className="hero-badge" style={{ backgroundColor: "#EBF5FB", color: "#0284c7" }}>Knowledge Base</span>
          <h2 className="about-title mt-3" style={{ color: "#000" }}>Understanding Melanoma</h2>
          <p className="mx-auto mt-3 mb-5" style={{ maxWidth: "700px", color: "#475569", fontSize: "1.1rem" }}>
            Explore our comprehensive educational resources to learn more about melanoma, risk factors, diagnosis, and how to cope.
          </p>
          <img src="/melanoma/knowledge.jpg" alt="A loving family walking together in a sunlit park" className="img-fluid rounded shadow-sm mb-4 knowledge-base-image" />
        </div>

        <div className="accordion-wrapper mx-auto animate-on-scroll delay-100" style={{ maxWidth: "900px" }}>
          {educationData.map((item, index) => {
            const isOpen = openSection === index;
            return (
              <Card
                key={index}
                className="mb-3 border-0 shadow-sm accordion-card-hover"
                style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #F1F5F9" }}
              >
                <CardHeader
                  className="bg-white p-0 border-0"
                  onClick={() => toggleSection(index)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex justify-content-between align-items-center p-4">
                    <h5 className="mb-0" style={{ fontWeight: "700", color: isOpen ? "#0284c7" : "#1E293B", transition: "color 0.3s ease" }}>
                      {item.title}
                    </h5>
                    <div style={{ color: isOpen ? "#0284c7" : "#94A3B8" }}>
                      {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </div>
                  </div>
                </CardHeader>
                <Collapse isOpen={isOpen}>
                  <CardBody className="pt-0 pb-4 px-4" style={{ fontSize: "1.05rem", color: "#475569", lineHeight: "1.9" }}>
                    <div className="border-top pt-4">
                      {item.content}
                    </div>
                  </CardBody>
                </Collapse>
              </Card>
            );
          })}
        </div>
      </Container>
    </div>
  );
};

export default MelanomaEducation;
